import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { matchAllCatalogueIds, resolveAnalysesToCatalogueIds } from '@/lib/analyseMatcher';
import { detecterTypeAnalyse, donneesRenseignees } from '@/lib/analysesSpeciales';
import { normaliserTexte } from '@/lib/normaliser';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import LabNavigation from '@/components/feature/LabNavigation';
import ResultatSpecifiqueInput from './components/ResultatSpecifiqueInput';

interface BonLaboratoire {
  id: string;
  type_analyse: string;
  date_prescription: string;
  medecin_id: string;
  medecin_nom: string;
  medecin_prenom: string;
  sage_femme_id: string | null;
  prescripteur_type: string;
  sage_femme_nom: string;
  sage_femme_prenom: string;
  analyses_ids: string[] | null;
}

interface AnalyseCatalogue {
  id: string;
  code: string;
  nom: string;
  unite: string;
  categorie: string;
  description: string | null;
  val_min_0_1_an: number | null;
  val_max_0_1_an: number | null;
  val_min_1_5_ans: number | null;
  val_max_1_5_ans: number | null;
  val_min_5_12_ans: number | null;
  val_max_5_12_ans: number | null;
  val_min_12_18_ans: number | null;
  val_max_12_18_ans: number | null;
  val_min_adulte_homme: number | null;
  val_max_adulte_homme: number | null;
  val_min_adulte_femme: number | null;
  val_max_adulte_femme: number | null;
  val_min_60_plus: number | null;
  val_max_60_plus: number | null;
}

interface PatientInfo {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string | null;
  sexe: string | null;
  numero_dossier: string | null;
  telephone: string | null;
}

interface ResultatAnterieur {
  id: string;
  valeur: number;
  dossier_id: string;
  created_at: string;
  dossier_date: string;
  analyse_code: string;
  analyse_nom: string;
  analyse_unite: string;
}

interface AnalyseSelectionnee {
  analyseId: string;
  valeur: string;
  donnees: Record<string, string>;
  fichierUrl: string | null;
  fichierNom: string | null;
}

const createEmptySelection = (analyseId: string): AnalyseSelectionnee => ({
  analyseId,
  valeur: '',
  donnees: {},
  fichierUrl: null,
  fichierNom: null,
});

export default function SaisirAnalysesPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedBonId = searchParams.get('bonId') || '';

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<PatientInfo[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [patientAge, setPatientAge] = useState<number | null>(null);
  const [searchError, setSearchError] = useState('');

  const [catalogue, setCatalogue] = useState<AnalyseCatalogue[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState('Toutes');
  const [catalogueSearchTerm, setCatalogueSearchTerm] = useState('');

  const [selectedAnalyses, setSelectedAnalyses] = useState<Map<string, AnalyseSelectionnee>>(new Map());
  const [resultatsAnterieurs, setResultatsAnterieurs] = useState<Map<string, ResultatAnterieur[]>>(new Map());
  const [loadingHistorique, setLoadingHistorique] = useState(false);

  const [bonsLaboratoire, setBonsLaboratoire] = useState<BonLaboratoire[]>([]);
  const [selectedBonId, setSelectedBonId] = useState<string>('');
  const [conclusion, setConclusion] = useState('');
  const [inclureResultatsAnterieurs, setInclureResultatsAnterieurs] = useState(false);
  const [loadingBons, setLoadingBons] = useState(false);

  const [laborantinId, setLaborantinId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const { mutate } = useOfflineMutation<any>();

  const [expandedAnalyse, setExpandedAnalyse] = useState<string | null>(null);

  const [allPatients, setAllPatients] = useState<PatientInfo[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<PatientInfo[]>([]);
  const [patientsLoading, setPatientsLoading] = useState(true);

  // Nom du médecin externe pour analyse directe
  const [prescripteurExterneNom, setPrescripteurExterneNom] = useState('');
  const [resolvingBonAnalyses, setResolvingBonAnalyses] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'laborantin' && role !== 'super_admin') { navigate('/', { replace: true }); return; }
    loadLaborantinId();
    loadCatalogue();
    loadPatients();
  }, [user, authLoading, role]);

  // Quand un bonId est passé dans l'URL, charger automatiquement le patient lié
  useEffect(() => {
    if (!preselectedBonId || !laborantinId || authLoading) return;
    autoSelectPatientFromBon(preselectedBonId);
  }, [preselectedBonId, laborantinId, authLoading]);

  // Réinitialiser la catégorie active quand on change de bon (ou passe en analyse directe)
  useEffect(() => {
    setActiveCategory('Toutes');
  }, [selectedBonId]);

  // Quand un bon est sélectionné, auto-remplir les analyses depuis le bon
  useEffect(() => {
    if (!patient || !selectedBonId || selectedBonId === 'direct' || catalogue.length === 0) return;

    const bon = bonsLaboratoire.find(b => b.id === selectedBonId);
    if (!bon) return;

    let cancelled = false;

    const autoSelect = async () => {
      let idsToSelect: string[] = [];

      // Résolution exacte depuis le libellé demandé (type_analyse). Toute analyse
      // absente du catalogue est créée telle quelle dans "Analyse générale", de sorte
      // que le nom affiché côté laborantin soit identique à celui demandé par le médecin.
      if (bon.type_analyse) {
        const labels = bon.type_analyse.split(';').map(l => l.trim()).filter(Boolean);
        setResolvingBonAnalyses(true);
        try {
          const resolved = await resolveAnalysesToCatalogueIds(labels, catalogue);
          if (cancelled) return;
          idsToSelect = resolved.ids;
          if (resolved.catalogue.length > catalogue.length) {
            setCatalogue(resolved.catalogue);
            const cats = Array.from(new Set(resolved.catalogue.map((a: any) => a.categorie).filter(Boolean)));
            setCategories(cats as string[]);
          }
        } catch {
          // Silencieux — le laborantin pourra sélectionner manuellement
        } finally {
          if (!cancelled) setResolvingBonAnalyses(false);
        }
      }

      if (cancelled) return;

      if (idsToSelect.length > 0) {
        setSelectedAnalyses(prev => {
          const newMap = new Map(prev);
          for (const id of idsToSelect) {
            if (!newMap.has(id)) {
              newMap.set(id, createEmptySelection(id));
              if (patient) {
                loadHistoriqueForAnalyseSilent(id);
              }
            }
          }
          return newMap;
        });
      }
    };

    autoSelect();
    return () => { cancelled = true; };
  }, [selectedBonId, catalogue, patient, bonsLaboratoire]);

  const autoSelectPatientFromBon = async (bonId: string) => {
    try {
      // Récupérer le bon pour avoir le patient_id
      const { data: bon, error: bonErr } = await supabase
        .from('bons_laboratoire')
        .select('patient_id, id, type_analyse, date_prescription, medecin_id, sage_femme_id, prescripteur_type')
        .eq('id', bonId)
        .eq('laborantin_id', laborantinId)
        .maybeSingle();

      if (bonErr || !bon) return;

      // Récupérer les infos du patient
      const { data: patientData, error: patientErr } = await supabase
        .from('patients')
        .select('id, nom, prenom, date_naissance, sexe, numero_dossier, telephone')
        .eq('id', bon.patient_id)
        .maybeSingle();

      if (patientErr || !patientData) return;

      // Auto-sélectionner le patient — ça déclenche loadBonsLaboratoire qui pré-sélectionne le bon
      selectPatient(patientData);
    } catch {
      // Silencieux — le laborantin pourra chercher manuellement
    }
  };

  const loadLaborantinId = async () => {
    const { data } = await supabase
      .from('laborantins').select('id').eq('user_id', user!.id).maybeSingle();
    if (data) setLaborantinId(data.id);
  };

  const loadCatalogue = async () => {
    const { data, error } = await supabase
      .from('analyses_biochimie_catalogue')
      .select('*')
      .order('categorie', { ascending: true })
      .order('nom', { ascending: true });

    if (!error && data) {
      setCatalogue(data);
      const cats = Array.from(new Set(data.map((a: AnalyseCatalogue) => a.categorie).filter(Boolean)));
      setCategories(cats as string[]);
    }
  };

  const loadPatients = async () => {
    setPatientsLoading(true);
    const { data, error } = await supabase
      .from('patients')
      .select('id, nom, prenom, date_naissance, sexe, numero_dossier, telephone')
      .order('nom', { ascending: true });

    if (!error && data) {
      setAllPatients(data);
      setFilteredPatients(data);
    }
    setPatientsLoading(false);
  };

  // Filtrage en temps réel des patients
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    const term = value.trim().toLowerCase();
    if (!term) {
      setFilteredPatients(allPatients);
      setShowDropdown(false);
      return;
    }
    const filtered = allPatients.filter(p => {
      const dossier = (p.numero_dossier || '').toLowerCase();
      const nom = (p.nom || '').toLowerCase();
      const prenom = (p.prenom || '').toLowerCase();
      return dossier.includes(term) || nom.includes(term) || prenom.includes(term) || `${prenom} ${nom}`.includes(term);
    });
    setFilteredPatients(filtered);
    if (filtered.length <= 5) {
      setShowDropdown(true);
    }
  };

  const getAgeRange = (age: number | null): string => {
    if (age === null) return 'adulte_homme';
    if (age < 1) return '0_1_an';
    if (age <= 5) return '1_5_ans';
    if (age <= 12) return '5_12_ans';
    if (age <= 18) return '12_18_ans';
    if (age > 60) return '60_plus';
    return 'adulte_homme';
  };

  const getValeursReference = (analyse: AnalyseCatalogue, age: number | null, sexe: string | null) => {
    const range = getAgeRange(age);

    const rangeMap: Record<string, { min: number | null; max: number | null }> = {
      '0_1_an': { min: analyse.val_min_0_1_an, max: analyse.val_max_0_1_an },
      '1_5_ans': { min: analyse.val_min_1_5_ans, max: analyse.val_max_1_5_ans },
      '5_12_ans': { min: analyse.val_min_5_12_ans, max: analyse.val_max_5_12_ans },
      '12_18_ans': { min: analyse.val_min_12_18_ans, max: analyse.val_max_12_18_ans },
      '60_plus': { min: analyse.val_min_60_plus, max: analyse.val_max_60_plus },
      'adulte_homme': {
        min: sexe === 'F' ? analyse.val_min_adulte_femme : analyse.val_min_adulte_homme,
        max: sexe === 'F' ? analyse.val_max_adulte_femme : analyse.val_max_adulte_homme,
      },
    };

    return rangeMap[range] || { min: null, max: null };
  };

  const handleSearchPatient = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setSearchError('Veuillez entrer un numéro de dossier ou un nom de patient');
      return;
    }

    setSearching(true);
    setSearchError('');
    setPatient(null);
    setPatientAge(null);
    setSelectedAnalyses(new Map());
    setResultatsAnterieurs(new Map());
    setSearchResults([]);
    setShowDropdown(false);

    try {
      // 1. Essayer par numéro de dossier
      const { data: byDossier, error: dossierErr } = await supabase
        .from('patients')
        .select('id, nom, prenom, date_naissance, sexe, numero_dossier, telephone')
        .ilike('numero_dossier', `%${term}%`)
        .maybeSingle();

      if (!dossierErr && byDossier) {
        selectPatient(byDossier);
        return;
      }

      // 2. Chercher par nom ou prénom
      const searchWords = term.split(/\s+/).filter(w => w.length > 0);
      let query = supabase
        .from('patients')
        .select('id, nom, prenom, date_naissance, sexe, numero_dossier, telephone');

      if (searchWords.length === 1) {
        const word = `%${searchWords[0]}%`;
        query = query.or(`nom.ilike.${word},prenom.ilike.${word}`);
      } else if (searchWords.length >= 2) {
        // Si deux mots, chercher (mot1 dans nom ET mot2 dans prenom) OU l'inverse
        const w1 = `%${searchWords[0]}%`;
        const w2 = `%${searchWords[1]}%`;
        query = query.or(
          `and(nom.ilike.${w1},prenom.ilike.${w2}),and(nom.ilike.${w2},prenom.ilike.${w1}),nom.ilike.${w1},prenom.ilike.${w1},nom.ilike.${w2},prenom.ilike.${w2}`
        );
      }

      const { data: byName, error: nameErr } = await query.limit(10);

      if (nameErr) throw nameErr;

      if (!byName || byName.length === 0) {
        setSearchError(`Aucun patient trouvé pour "${term}"`);
      } else if (byName.length === 1) {
        selectPatient(byName[0]);
      } else {
        setSearchResults(byName);
        setShowDropdown(true);
      }
    } catch (err: any) {
      setSearchError(err.message || 'Erreur lors de la recherche');
    } finally {
      setSearching(false);
    }
  };

  const selectPatient = (p: PatientInfo) => {
    setPatient(p);
    setSearchResults([]);
    setShowDropdown(false);
    setSearchError('');
    setSelectedBonId('');
    setConclusion('');
    setInclureResultatsAnterieurs(false);
    setBonsLaboratoire([]);
    if (p.date_naissance) {
      const birth = new Date(p.date_naissance);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      setPatientAge(age);
    }
    loadBonsLaboratoire(p.id);
  };

  const loadBonsLaboratoire = async (patientId: string) => {
    setLoadingBons(true);
    try {
      const { data, error } = await supabase
        .from('bons_laboratoire')
        .select('id, type_analyse, date_prescription, medecin_id, sage_femme_id, prescripteur_type, analyses_ids, medecins(nom, prenom), sage_femmes(nom, prenom)')
        .eq('patient_id', patientId)
        .eq('laborantin_id', laborantinId)
        .eq('statut', 'en_cours')
        .order('date_prescription', { ascending: false });

      if (!error && data) {
        const formatted: BonLaboratoire[] = data.map((b: any) => ({
          id: b.id,
          type_analyse: b.type_analyse,
          date_prescription: b.date_prescription,
          medecin_id: b.medecin_id,
          medecin_nom: b.medecins?.nom || '',
          medecin_prenom: b.medecins?.prenom || '',
          sage_femme_id: b.sage_femme_id,
          prescripteur_type: b.prescripteur_type || 'medecin',
          sage_femme_nom: b.sage_femmes?.nom || '',
          sage_femme_prenom: b.sage_femmes?.prenom || '',
          analyses_ids: b.analyses_ids || null,
        }));
        setBonsLaboratoire(formatted);
        // Pre-selectionner le bon si passe en parametre URL
        if (preselectedBonId && formatted.find(b => b.id === preselectedBonId)) {
          setSelectedBonId(preselectedBonId);
        } else if (formatted.length === 1) {
          setSelectedBonId(formatted[0].id);
        }
      }
    } catch {
      // Silencieux
    } finally {
      setLoadingBons(false);
    }
  };

  const handleToggleAnalyse = useCallback(async (analyseId: string) => {
    setSelectedAnalyses(prev => {
      const newMap = new Map(prev);
      if (newMap.has(analyseId)) {
        newMap.delete(analyseId);
      } else {
        newMap.set(analyseId, createEmptySelection(analyseId));
        if (patient) {
          loadHistoriqueForAnalyse(analyseId);
        }
      }
      return newMap;
    });
  }, [patient]);

  const loadHistoriqueForAnalyse = async (analyseId: string) => {
    if (!patient) return;
    const analyse = catalogue.find(a => a.id === analyseId);
    if (!analyse) return;

    setLoadingHistorique(true);
    try {
      const { data, error } = await supabase
        .from('resultats_analyses')
        .select(`
          id, valeur, dossier_id, created_at,
          dossiers_analyses!inner(date_analyse, patient_id)
        `)
        .eq('analyse_id', analyseId)
        .eq('dossiers_analyses.patient_id', patient.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!error && data) {
        const formatted: ResultatAnterieur[] = data.map((r: any) => ({
          id: r.id,
          valeur: r.valeur,
          dossier_id: r.dossier_id,
          created_at: r.created_at,
          dossier_date: r.dossiers_analyses?.date_analyse || r.created_at,
          analyse_code: analyse.code,
          analyse_nom: analyse.nom,
          analyse_unite: analyse.unite,
        }));
        setResultatsAnterieurs(prev => {
          const newMap = new Map(prev);
          newMap.set(analyseId, formatted);
          return newMap;
        });
      }
    } catch {
      // Silencieux
    } finally {
      setLoadingHistorique(false);
    }
  };

  const loadHistoriqueForAnalyseSilent = async (analyseId: string) => {
    if (!patient) return;
    const analyse = catalogue.find(a => a.id === analyseId);
    if (!analyse) return;

    try {
      const { data, error } = await supabase
        .from('resultats_analyses')
        .select(`
          id, valeur, dossier_id, created_at,
          dossiers_analyses!inner(date_analyse, patient_id)
        `)
        .eq('analyse_id', analyseId)
        .eq('dossiers_analyses.patient_id', patient.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!error && data) {
        const formatted: ResultatAnterieur[] = data.map((r: any) => ({
          id: r.id,
          valeur: r.valeur,
          dossier_id: r.dossier_id,
          created_at: r.created_at,
          dossier_date: r.dossiers_analyses?.date_analyse || r.created_at,
          analyse_code: analyse.code,
          analyse_nom: analyse.nom,
          analyse_unite: analyse.unite,
        }));
        setResultatsAnterieurs(prev => {
          const newMap = new Map(prev);
          newMap.set(analyseId, formatted);
          return newMap;
        });
      }
    } catch {
      // Silencieux
    }
  };

  const handleValeurChange = (analyseId: string, valeur: string) => {
    setSelectedAnalyses(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(analyseId);
      if (existing) {
        newMap.set(analyseId, { ...existing, valeur });
      }
      return newMap;
    });
  };

  const handleDonneesChange = (analyseId: string, donnees: Record<string, string>) => {
    setSelectedAnalyses(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(analyseId);
      if (existing) {
        newMap.set(analyseId, { ...existing, donnees });
      }
      return newMap;
    });
  };

  const handleFichierChange = (analyseId: string, url: string | null, nom: string | null) => {
    setSelectedAnalyses(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(analyseId);
      if (existing) {
        newMap.set(analyseId, { ...existing, fichierUrl: url, fichierNom: nom });
      }
      return newMap;
    });
  };

  const handleFinalizeResults = async () => {
    if (!patient) return;

    const isDirect = selectedBonId === 'direct';

    if (!isDirect && !selectedBonId) {
      setSaveError('Veuillez sélectionner un bon de laboratoire ou choisir "Analyse directe" avant de finaliser les résultats');
      return;
    }

    const entriesWithValues = Array.from(selectedAnalyses.entries())
      .map(([analyseId, sel]) => {
        const analyse = catalogue.find(a => a.id === analyseId);
        const type = analyse ? detecterTypeAnalyse(analyse.code) : 'simple';
        const hasValeur = sel.valeur.trim() !== '';
        const hasDonnees = donneesRenseignees(sel.donnees);
        const hasFichier = !!sel.fichierUrl;
        if (!hasValeur && !hasDonnees && !hasFichier) return null;

        const numVal = parseFloat(sel.valeur);
        const entry: any = {
          analyse_id: analyseId,
          valeur: hasValeur ? (isNaN(numVal) ? null : numVal) : null,
          valeur_texte: hasValeur ? sel.valeur.trim() : null,
        };

        if (type === 'nfs' || type === 'atb' || type === 'ecbu') {
          const donnees = Object.fromEntries(
            Object.entries(sel.donnees || {}).filter(([, v]) => (v || '').trim() !== ''),
          );
          entry.donnees = donnees;
        }

        if (hasFichier) {
          entry.fichier_url = sel.fichierUrl;
        }

        return entry;
      })
      .filter((e): e is any => e !== null);

    if (entriesWithValues.length === 0) {
      setSaveError('Veuillez saisir au moins une valeur, un résultat structuré ou joindre un fichier pour une analyse sélectionnée');
      return;
    }

    setSaving(true);
    setSaveError('');
    setSaveSuccess('');
    const online = navigator.onLine;

    try {
      const { data: laborantinData } = online ? await supabase
        .from('laborantins')
        .select('id')
        .eq('user_id', user!.id)
        .maybeSingle() : { data: null };

      const dossierInsert: any = {
        patient_id: patient.id,
        laborantin_id: laborantinData?.id || laborantinId,
        date_analyse: new Date().toISOString(),
        statut: 'brouillon',
        conclusion: conclusion.trim() || null,
        bon_laboratoire_id: isDirect ? null : (selectedBonId || null),
        inclure_resultats_anterieurs: inclureResultatsAnterieurs,
        prescripteur_externe_nom: isDirect && prescripteurExterneNom.trim() ? prescripteurExterneNom.trim() : null,
      };

      // Étape 1 : Créer le dossier d'analyses (offline-first)
      const dossierResult = await mutate({
        table: 'dossiers_analyses',
        operation: 'INSERT',
        data: dossierInsert,
        metadata: {
          summary: `Dossier analyses — ${patient.prenom} ${patient.nom} (${entriesWithValues.length} résultat(s))`,
          performedBy: 'laborantin',
        },
        onlineExecutor: async () => {
          const { data: dossierData, error: dossierError } = await supabase
            .from('dossiers_analyses')
            .insert(dossierInsert)
            .select('id')
            .single();

          if (dossierError) throw dossierError;

          // Étape 2 : Insérer les résultats (en ligne seulement, dans l'executor)
          const resultsToInsert = entriesWithValues.map(e => ({
            dossier_id: dossierData.id,
            analyse_id: e.analyse_id,
            valeur: e.valeur,
            valeur_texte: e.valeur_texte,
            donnees: e.donnees ?? null,
            fichier_url: e.fichier_url ?? null,
          }));

          const { error: resultsError } = await supabase
            .from('resultats_analyses')
            .insert(resultsToInsert);

          if (resultsError) throw resultsError;
          return dossierData;
        },
      });

      // Étape 2 (offline) : Insérer les résultats liés au dossier
      // Note: la sync queue détecte automatiquement les chaînes FK (dossier_id → dossiers_analyses)
      if (!online) {
        for (const entry of entriesWithValues) {
          await mutate({
            table: 'resultats_analyses',
            operation: 'INSERT',
            data: {
              dossier_id: dossierResult.id,
              analyse_id: entry.analyse_id,
              valeur: entry.valeur,
              valeur_texte: entry.valeur_texte,
              donnees: entry.donnees ?? null,
              fichier_url: entry.fichier_url ?? null,
            },
            metadata: {
              summary: `Résultat analyse #${entry.analyse_id}`,
              performedBy: 'laborantin',
            },
            onlineExecutor: async () => {
              const { data: resData, error: resErr } = await supabase
                .from('resultats_analyses')
                .insert({
                  dossier_id: dossierResult.id,
                  analyse_id: entry.analyse_id,
                  valeur: entry.valeur,
                  valeur_texte: entry.valeur_texte,
                  donnees: entry.donnees ?? null,
                  fichier_url: entry.fichier_url ?? null,
                })
                .select()
                .single();
              if (resErr) throw resErr;
              return resData;
            },
          });
        }
      }

      if (!online) {
        setSaveSuccess('Brouillon enregistré en mode hors-ligne — synchronisation automatique au retour de la connexion.');
        setTimeout(() => {
          navigate(`/laborantin/rapport-analyses/${dossierResult.id}`);
        }, 1500);
      } else {
        setSaveSuccess('Brouillon enregistré — ouverture du rapport...');
        setTimeout(() => {
          navigate(`/laborantin/rapport-analyses/${dossierResult.id}`);
        }, 600);
      }
    } catch (err: any) {
      setSaveError(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleEnvoyerMedecin = () => handleFinalizeResults();
  const handleImprimer = () => handleFinalizeResults();

  const getCategorieIcon = (cat?: string) => {
    if (!cat) return 'ri-flask-line';
    const lowerCat = cat.toLowerCase();
    if (lowerCat.includes('rénal') || lowerCat.includes('renal')) return 'ri-drop-line';
    if (lowerCat.includes('hépat') || lowerCat.includes('hepat')) return 'ri-mental-health-line';
    if (lowerCat.includes('lipid') || lowerCat.includes('cholest')) return 'ri-heart-pulse-line';
    if (lowerCat.includes('enzyme')) return 'ri-test-tube-line';
    if (lowerCat.includes('hormone') || lowerCat.includes('thyro')) return 'ri-psychotherapy-line';
    if (lowerCat.includes('ionogramme') || lowerCat.includes('électro')) return 'ri-flashlight-line';
    if (lowerCat.includes('inflammatoire') || lowerCat.includes('inflammation')) return 'ri-virus-line';
    if (lowerCat.includes('protéine') || lowerCat.includes('proteine')) return 'ri-dna-line';
    if (lowerCat.includes('pancréat') || lowerCat.includes('pancreat')) return 'ri-restaurant-line';
    if (lowerCat.includes('hémostase') || lowerCat.includes('hemostase') || lowerCat.includes('coagulation')) return 'ri-contrast-drop-2-line';
    if (lowerCat.includes('hématologie') || lowerCat.includes('hematologie')) return 'ri-drop-fill';
    if (lowerCat.includes('sang') || lowerCat.includes('glyc')) return 'ri-contrast-drop-line';
    if (lowerCat.includes('minéral') || lowerCat.includes('mineral') || lowerCat.includes('martial')) return 'ri-capsule-line';
    if (lowerCat.includes('tumoral') || lowerCat.includes('marqueur')) return 'ri-microscope-line';
    if (lowerCat.includes('sérologie') || lowerCat.includes('serologie')) return 'ri-shield-line';
    if (lowerCat.includes('bactériologie') || lowerCat.includes('bacteriologie')) return 'ri-bug-line';
    if (lowerCat.includes('combiné') || lowerCat.includes('bilan')) return 'ri-file-list-3-line';
    if (lowerCat.includes('musculaire')) return 'ri-run-line';
    return 'ri-flask-line';
  };

  const formaterDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formaterDateHeure = (d: string) => {
    return new Date(d).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const getAgeLabel = (age: number | null): string => {
    if (age === null) return 'Adulte';
    if (age < 1) return `${Math.floor(age * 12)} mois`;
    return `${age} an${age > 1 ? 's' : ''}`;
  };

  // Récupérer le type_analyse du bon sélectionné
  const selectedBonTypeAnalyse = useMemo(() => {
    if (!selectedBonId || selectedBonId === 'direct') return null;
    const bon = bonsLaboratoire.find(b => b.id === selectedBonId);
    return bon?.type_analyse || null;
  }, [selectedBonId, bonsLaboratoire]);

  // Catalogue filtré : par bon ET par catégorie si applicable
  const displayedCatalogue = useMemo(() => {
    let filtered = catalogue;
    if (selectedBonTypeAnalyse) {
      const matchingIds = new Set(matchAllCatalogueIds(selectedBonTypeAnalyse, catalogue));
      filtered = catalogue.filter(a => matchingIds.has(a.id));
    }

    if (activeCategory !== 'Toutes') {
      filtered = filtered.filter(a => a.categorie === activeCategory);
    }

    if (catalogueSearchTerm.trim()) {
      const q = normaliserTexte(catalogueSearchTerm.trim());
      filtered = filtered.filter(a =>
        normaliserTexte(a.nom).includes(q) || normaliserTexte(a.code || '').includes(q)
      );
    }

    return filtered;
  }, [catalogue, selectedBonTypeAnalyse, activeCategory, catalogueSearchTerm]);

  // Catégories disponibles dans le catalogue filtré
  const displayedCategories = useMemo(() => {
    const raw = selectedBonTypeAnalyse
      ? Array.from(new Set(displayedCatalogue.map(a => a.categorie)))
      : categories;
    return raw.filter((c): c is string => !!c);
  }, [displayedCatalogue, selectedBonTypeAnalyse, categories]);

  // Le bon sélectionné complet (pour affichage du type d'analyse dans l'en-tête)
  const selectedBon = useMemo(() => {
    if (!selectedBonId || selectedBonId === 'direct') return null;
    return bonsLaboratoire.find(b => b.id === selectedBonId) || null;
  }, [selectedBonId, bonsLaboratoire]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
      </div>
    );
  }



  const selectedCount = selectedAnalyses.size;
  const selectedWithValues = Array.from(selectedAnalyses.entries())
    .filter(([_, s]) => s.valeur.trim() !== '' || donneesRenseignees(s.donnees) || !!s.fichierUrl).length;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* En-tête */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Saisie d'analyses de laboratoire</h1>
                <p className="text-gray-500 text-sm mt-1">
                  Sélectionnez un patient dans la liste ci-dessous, puis choisissez les analyses parmi les 179 analyses du catalogue (biochimie, hématologie, hémostase, hormones, marqueurs, sérologie, bactériologie...)
                </p>
              </div>
              <LabNavigation />
            </div>
          </div>

          {/* Recherche patient */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-search-line text-teal-600"></i>
              Filtrer les patients
            </h2>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <i className="ri-folder-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilteredPatients(allPatients);
                    }}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className="ri-close-circle-line text-lg"></i>
                  </button>
                )}
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Filtrer par numéro de dossier ou nom du patient..."
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                />
              </div>
            </div>

            {searchError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
                <i className="ri-error-warning-line"></i>
                {searchError}
              </div>
            )}

            {/* Fiche patient */}
            {patient && (
              <div className="mt-4 bg-teal-50 border border-teal-200 rounded-2xl p-4 md:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-teal-700 font-bold text-lg">
                        {patient.prenom?.[0]}{patient.nom?.[0]}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 text-lg">
                        {patient.prenom} {patient.nom}
                      </h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-0.5">
                        <span><i className="ri-folder-line mr-1"></i>{patient.numero_dossier}</span>
                        {patientAge !== null && (
                          <span><i className="ri-calendar-line mr-1"></i>{getAgeLabel(patientAge)}</span>
                        )}
                        {patient.sexe && (
                          <span>
                            <i className={patient.sexe === 'F' ? 'ri-women-line' : 'ri-men-line'}></i>
                            {' '}{patient.sexe === 'F' ? 'Femme' : 'Homme'}
                          </span>
                        )}
                        {patient.telephone && (
                          <span><i className="ri-phone-line mr-1"></i>{patient.telephone}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setPatient(null);
                      setPatientAge(null);
                      setSelectedAnalyses(new Map());
                      setResultatsAnterieurs(new Map());
                      setSearchTerm('');
                      setFilteredPatients(allPatients);
                      setSelectedBonId('');
                      setConclusion('');
                      setBonsLaboratoire([]);
                    }}
                    className="text-sm text-gray-500 hover:text-red-500 cursor-pointer transition-colors flex items-center gap-1"
                  >
                    <i className="ri-close-line"></i>Changer de patient
                  </button>
                </div>
              </div>
            )}
          </div>

          {patient && (
            <>
              {/* Bons de laboratoire + Analyse directe */}
              <div className="mt-4">
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <i className="ri-file-list-3-line text-teal-600"></i>
                    Source de la demande d'analyse
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {bonsLaboratoire.map(bon => {
                      const isSageFemme = bon.prescripteur_type === 'sage_femme';
                      return (
                      <button
                        key={bon.id}
                        onClick={() => setSelectedBonId(bon.id)}
                        className={`flex flex-col items-start px-4 py-3 rounded-xl border cursor-pointer transition-all text-left ${
                          selectedBonId === bon.id
                            ? 'border-teal-500 bg-teal-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-sm font-semibold text-gray-800">{bon.type_analyse}</span>
                        <span className="text-xs text-gray-500 mt-0.5">
                          {isSageFemme ? (
                            <>Sage-femme {bon.sage_femme_prenom} {bon.sage_femme_nom}</>
                          ) : (
                            <>Dr. {bon.medecin_prenom} {bon.medecin_nom}</>
                          )}
                        </span>
                        <span className="text-[10px] text-gray-400 mt-0.5">
                          Prescrit le {new Date(bon.date_prescription).toLocaleDateString('fr-FR')}
                        </span>
                      </button>
                    )})}

                    {/* Option Analyse directe (sans prescription) */}
                    <button
                      onClick={() => {
                        setSelectedBonId('direct');
                        setPrescripteurExterneNom('');
                      }}
                      className={`flex flex-col items-start px-4 py-3 rounded-xl border cursor-pointer transition-all text-left ${
                        selectedBonId === 'direct'
                          ? 'border-teal-500 bg-teal-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        <i className="ri-flask-line text-teal-600"></i>
                        Analyse directe
                      </span>
                      <span className="text-xs text-gray-500 mt-0.5">Prescription externe à la clinique</span>
                      <span className="text-[10px] text-gray-400 mt-0.5">Demande apportée par le patient</span>
                    </button>
                  </div>

                  {/* Champ nom du médecin externe (visible seulement si analyse directe) */}
                  {selectedBonId === 'direct' && (
                    <div className="mt-2 p-3 bg-teal-50/50 border border-teal-200 rounded-xl">
                      <label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <i className="ri-stethoscope-line text-teal-600"></i>
                        Nom du médecin prescripteur externe
                      </label>
                      <input
                        type="text"
                        value={prescripteurExterneNom}
                        onChange={(e) => setPrescripteurExterneNom(e.target.value)}
                        placeholder="Ex: Dr. KOUASSI Amenan (prescription externe)"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                      />
                      <p className="text-[11px] text-gray-400 mt-1">
                        Précisez le nom du médecin externe ayant demandé cette analyse (facultatif)
                      </p>
                    </div>
                  )}

                  {bonsLaboratoire.length === 0 && (
                    <p className="text-xs text-gray-500 italic bg-gray-50 rounded-lg px-3 py-2">
                      <i className="ri-information-line mr-1"></i>
                      Aucun bon de laboratoire en attente pour ce patient. Vous pouvez effectuer une <strong>analyse directe</strong> sans prescription.
                    </p>
                  )}
                </div>
                {loadingBons && (
                  <div className="flex items-center gap-2 text-sm text-gray-400 mt-2">
                    <i className="ri-loader-4-line animate-spin"></i>Chargement des bons de laboratoire...
                  </div>
                )}
              </div>

              {/* Catalogue */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6 mb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <i className={`${selectedBonTypeAnalyse ? 'ri-file-list-3-line text-teal-600' : 'ri-list-check-3 text-teal-600'}`}></i>
                      {selectedBonTypeAnalyse ? (
                        <>
                          Analyses demandées : <span className="text-teal-700">{selectedBonTypeAnalyse}</span>
                        </>
                      ) : selectedBonId === 'direct' ? (
                        'Catalogue complet des analyses de laboratoire'
                      ) : (
                        'Catalogue complet des analyses de laboratoire'
                      )}
                    </h2>
                    {selectedBon && (
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <span className="bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full text-[11px] font-medium">
                          {displayedCatalogue.length} analyse{displayedCatalogue.length > 1 ? 's' : ''} correspondante{displayedCatalogue.length > 1 ? 's' : ''}
                        </span>
                        <span>— Prescription du {(() => { const d = new Date(selectedBon.date_prescription); return d.toLocaleDateString('fr-FR'); })()}</span>
                        {selectedBon.prescripteur_type === 'sage_femme' ? (
                          <span>par Sage-femme {selectedBon.sage_femme_prenom} {selectedBon.sage_femme_nom}</span>
                        ) : (
                          <span>par Dr. {selectedBon.medecin_prenom} {selectedBon.medecin_nom}</span>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {selectedCount} analyse{selectedCount > 1 ? 's' : ''} sélectionnée{selectedCount > 1 ? 's' : ''}
                    {selectedWithValues > 0 && ` · ${selectedWithValues} valeur${selectedWithValues > 1 ? 's' : ''} saisie${selectedWithValues > 1 ? 's' : ''}`}
                  </div>
                </div>

                {/* Filtre catégories — masqué quand un bon spécifique est sélectionné (filtrage automatique) */}
                {!selectedBonTypeAnalyse && (
                <div className="flex flex-wrap gap-2 mb-5">
                  <button
                    onClick={() => setActiveCategory('Toutes')}
                    className={`px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                      activeCategory === 'Toutes'
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Toutes ({catalogue.length})
                  </button>
                  {categories.map(cat => {
                    const count = catalogue.filter(a => a.categorie === cat).length;
                    return (
                      <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        className={`px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all flex items-center gap-1.5 ${
                          activeCategory === cat
                            ? 'bg-teal-600 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <i className={`${getCategorieIcon(cat)} text-xs`}></i>
                        {cat}
                        <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                          activeCategory === cat ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-500'
                        }`}>{count}</span>
                      </button>
                    );
                  })}
                </div>
                )}

                {/* Filtre catégories réduit — quand un bon est sélectionné, montrer uniquement les catégories correspondantes */}
                {selectedBonTypeAnalyse && displayedCategories.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-5">
                  <button
                    onClick={() => setActiveCategory('Toutes')}
                    className={`px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                      activeCategory === 'Toutes'
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Toutes ({displayedCatalogue.length})
                  </button>
                  {displayedCategories.map(cat => {
                    const count = displayedCatalogue.filter(a => a.categorie === cat).length;
                    return (
                      <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        className={`px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all flex items-center gap-1.5 ${
                          activeCategory === cat
                            ? 'bg-teal-600 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <i className={`${getCategorieIcon(cat)} text-xs`}></i>
                        {cat}
                        <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                          activeCategory === cat ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-500'
                        }`}>{count}</span>
                      </button>
                    );
                  })}
                </div>
                )}

                {/* Recherche dans le catalogue */}
                <div className="relative mb-4">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="text"
                    value={catalogueSearchTerm}
                    placeholder="Rechercher une analyse..."
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                    onChange={(e) => {
                      setCatalogueSearchTerm(e.target.value);
                      setActiveCategory('Toutes');
                    }}
                  />
                </div>

                {/* Liste des analyses */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[600px] overflow-y-auto pr-1">
                  {resolvingBonAnalyses ? (
                    <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400">
                      <i className="ri-loader-4-line text-4xl mb-3 animate-spin"></i>
                      <p className="font-medium text-gray-500">Préparation des analyses demandées...</p>
                    </div>
                  ) : displayedCatalogue.length === 0 ? (
                    <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400">
                      <i className="ri-inbox-line text-4xl mb-3"></i>
                      <p className="font-medium text-gray-500">Aucune analyse trouvée</p>
                      <p className="text-sm mt-1">
                        {selectedBonTypeAnalyse
                          ? `Le type d'analyse "${selectedBonTypeAnalyse}" ne correspond à aucune analyse du catalogue.`
                          : 'Essayez de modifier votre recherche ou votre filtre.'}
                      </p>
                      {selectedBonTypeAnalyse && (
                        <button
                          onClick={() => setSelectedBonId('direct')}
                          className="mt-3 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer flex items-center gap-1"
                        >
                          <i className="ri-flask-line"></i>
                          Passer en analyse directe (catalogue complet)
                        </button>
                      )}
                    </div>
                  ) : (
                  displayedCatalogue.map(analyse => {
                    const isSelected = selectedAnalyses.has(analyse.id);
                    const ref = getValeursReference(analyse, patientAge, patient.sexe);
                    const historique = resultatsAnterieurs.get(analyse.id) || [];
                    const typeAnalyse = detecterTypeAnalyse(analyse.code);

                    return (
                      <div key={analyse.id}>
                        {/* Row compacte */}
                        <div
                          className={`flex items-center gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-all ${
                            isSelected
                              ? 'border-teal-400 bg-teal-50/80'
                              : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50'
                          }`}
                          onClick={() => handleToggleAnalyse(analyse.id)}
                        >
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected ? 'bg-teal-600 border-teal-600' : 'border-gray-300'
                          }`}>
                            {isSelected && <i className="ri-check-line text-white text-xs"></i>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{analyse.nom}</p>
                            <p className="text-[11px] text-gray-400">
                              {analyse.code}{analyse.unite ? ` · ${analyse.unite}` : ''}
                              {ref.min !== null && ref.max !== null && (
                                <span className="text-teal-600 ml-1">
                                  [{ref.min}–{ref.max}]
                                </span>
                              )}
                              {ref.min === null && ref.max !== null && (
                                <span className="text-teal-600 ml-1">
                                  [&lt;{ref.max}]
                                </span>
                              )}
                              {ref.min !== null && ref.max === null && (
                                <span className="text-teal-600 ml-1">
                                  [&gt;{ref.min}]
                                </span>
                              )}
                            </p>
                          </div>
                          {historique.length > 0 && (
                            <span className="flex-shrink-0 w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center text-[10px] text-amber-700 font-bold">
                              {historique.length}
                            </span>
                          )}
                        </div>

                        {/* Zone de saisie (si sélectionnée) */}
                        {isSelected && (
                          <div className="mt-1 ml-7 bg-gray-50 rounded-xl p-3 border border-gray-200">
                            {/* Valeurs de référence */}
                            {typeAnalyse === 'simple' && (
                            <div className="flex items-center gap-2 mb-2 text-xs">
                              <span className="text-gray-500">Réf. {getAgeLabel(patientAge)} :</span>
                              {ref.min !== null && ref.max !== null ? (
                                <span className="font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
                                  {ref.min} – {ref.max} {analyse.unite}
                                </span>
                              ) : ref.max !== null ? (
                                <span className="font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
                                  &lt; {ref.max} {analyse.unite}
                                </span>
                              ) : ref.min !== null ? (
                                <span className="font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
                                  &gt; {ref.min} {analyse.unite}
                                </span>
                              ) : (
                                <span className="text-gray-400 italic">Non défini</span>
                              )}
                            </div>
                            )}

                            {/* Historique */}
                            {typeAnalyse === 'simple' && historique.length > 0 && (
                              <div className="mb-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedAnalyse(expandedAnalyse === analyse.id ? null : analyse.id);
                                  }}
                                  className="text-xs text-amber-700 font-medium flex items-center gap-1 cursor-pointer hover:text-amber-800"
                                >
                                  <i className="ri-history-line"></i>
                                  Historique ({historique.length} résultat{historique.length > 1 ? 's' : ''})
                                  <i className={expandedAnalyse === analyse.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
                                </button>

                                {expandedAnalyse === analyse.id && (
                                  <div className="mt-2 space-y-1.5 max-h-32 overflow-y-auto">
                                    {historique.map((r, idx) => (
                                      <div key={r.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-1.5 text-xs border border-gray-100">
                                        <span className="text-gray-500">{formaterDateHeure(r.dossier_date)}</span>
                                        <span className={`font-bold ${(() => {
                                          const refVals = getValeursReference(analyse, patientAge, patient.sexe);
                                          if (refVals.min !== null && refVals.max !== null) {
                                            if (r.valeur < refVals.min) return 'text-amber-600';
                                            if (r.valeur > refVals.max) return 'text-red-600';
                                            return 'text-green-600';
                                          }
                                          if (refVals.max !== null) {
                                            if (r.valeur > refVals.max) return 'text-red-600';
                                            return 'text-green-600';
                                          }
                                          if (refVals.min !== null) {
                                            if (r.valeur < refVals.min) return 'text-amber-600';
                                            return 'text-green-600';
                                          }
                                          return 'text-gray-700';
                                        })()}`}>
                                          {r.valeur} {analyse.unite}
                                        </span>
                                        <span className="text-[10px] text-gray-400">
                                          {idx === 0 ? 'Plus récent' : `N-${idx}`}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Champ de saisie / saisie spécialisée */}
                            {typeAnalyse === 'simple' ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={selectedAnalyses.get(analyse.id)?.valeur || ''}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleValeurChange(analyse.id, e.target.value);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  placeholder={`Valeur${analyse.unite ? ` (${analyse.unite})` : ''}`}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                />
                                {analyse.unite && <span className="text-xs text-gray-400 whitespace-nowrap">{analyse.unite}</span>}
                              </div>
                            ) : (
                              <ResultatSpecifiqueInput
                                type={typeAnalyse}
                                valeur={selectedAnalyses.get(analyse.id)?.valeur || ''}
                                donnees={selectedAnalyses.get(analyse.id)?.donnees || {}}
                                fichierUrl={selectedAnalyses.get(analyse.id)?.fichierUrl || null}
                                fichierNom={selectedAnalyses.get(analyse.id)?.fichierNom || null}
                                unite={analyse.unite}
                                onValeurChange={(v) => handleValeurChange(analyse.id, v)}
                                onDonneesChange={(d) => handleDonneesChange(analyse.id, d)}
                                onFichierChange={(url, nom) => handleFichierChange(analyse.id, url, nom)}
                              />
                            )}

                            {/* Indicateur anormal - uniquement pour valeurs numeriques */}
                            {typeAnalyse === 'simple' && (() => {
                              const val = selectedAnalyses.get(analyse.id)?.valeur;
                              if (!val || val === '') return null;
                              const numVal = parseFloat(val);
                              if (isNaN(numVal)) return (
                                <p className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1">
                                  <i className="ri-file-text-line"></i>Valeur texte — pas de comparaison aux normes
                                </p>
                              );
                              if (ref.min !== null && ref.max !== null) {
                                if (numVal < ref.min) return (
                                  <p className="text-[11px] text-amber-600 mt-1.5 flex items-center gap-1">
                                    <i className="ri-arrow-down-line"></i>En dessous de la normale
                                  </p>
                                );
                                if (numVal > ref.max) return (
                                  <p className="text-[11px] text-red-600 mt-1.5 flex items-center gap-1">
                                    <i className="ri-arrow-up-line"></i>Au-dessus de la normale
                                  </p>
                                );
                                return (
                                  <p className="text-[11px] text-green-600 mt-1.5 flex items-center gap-1">
                                    <i className="ri-check-line"></i>Dans les normes
                                  </p>
                                );
                              }
                              if (ref.max !== null) {
                                if (numVal > ref.max) return (
                                  <p className="text-[11px] text-red-600 mt-1.5 flex items-center gap-1">
                                    <i className="ri-arrow-up-line"></i>Au-dessus de la normale
                                  </p>
                                );
                                return (
                                  <p className="text-[11px] text-green-600 mt-1.5 flex items-center gap-1">
                                    <i className="ri-check-line"></i>Dans les normes
                                  </p>
                                );
                              }
                              if (ref.min !== null) {
                                if (numVal < ref.min) return (
                                  <p className="text-[11px] text-amber-600 mt-1.5 flex items-center gap-1">
                                    <i className="ri-arrow-down-line"></i>En dessous de la normale
                                  </p>
                                );
                                return (
                                  <p className="text-[11px] text-green-600 mt-1.5 flex items-center gap-1">
                                    <i className="ri-check-line"></i>Dans les normes
                                  </p>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  }))}
                </div>
              </div>

              {/* Conclusion du biologiste médical */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <i className="ri-stethoscope-line text-teal-600"></i>
                  Conclusion du biologiste médical
                </h2>

                {/* Checkbox résultats antérieurs */}
                <label className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={inclureResultatsAnterieurs}
                    onChange={(e) => setInclureResultatsAnterieurs(e.target.checked)}
                    className="w-5 h-5 rounded-md border-2 border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer accent-teal-600"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-800">Inclure les résultats antérieurs dans le rapport</span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Les résultats précédents du patient seront affichés en dessous des résultats actuels pour chaque analyse
                    </p>
                  </div>
                </label>

                <textarea
                  value={conclusion}
                  onChange={(e) => setConclusion(e.target.value)}
                  rows={5}
                  placeholder="Rédigez ici la conclusion du biologiste médical suite à l'analyse des résultats..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                  maxLength={2000}
                />
                <p className="text-xs text-gray-400 mt-2 text-right">{conclusion.length}/2000 caractères</p>
              </div>

              {/* Barre d'actions en bas (sticky) */}
              <div className="sticky bottom-4 bg-white rounded-2xl border border-gray-200 p-4 shadow-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="text-sm text-gray-600">
                  {!selectedBonId ? (
                    <span className="text-amber-600 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      Sélectionnez un bon ou "Analyse directe"
                    </span>
                  ) : selectedWithValues > 0 ? (
                    <span>
                      <strong className="text-teal-700">{selectedWithValues}</strong> valeur{selectedWithValues > 1 ? 's' : ''} prête{selectedWithValues > 1 ? 's' : ''}
                      {selectedBonId === 'direct' && (
                        <span className="text-teal-600 ml-1">· Analyse directe</span>
                      )}
                      {selectedWithValues < selectedCount && (
                        <span className="text-amber-600 ml-1">
                          · {selectedCount - selectedWithValues} analyse{selectedCount - selectedWithValues > 1 ? 's' : ''} sans valeur
                        </span>
                      )}
                    </span>
                  ) : selectedCount > 0 ? (
                    <span className="text-amber-600">Saisissez les valeurs pour les analyses sélectionnées</span>
                  ) : (
                    <span className="text-gray-400">Sélectionnez des analyses dans le catalogue ci-dessus</span>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {saveError && (
                    <span className="text-sm text-red-600 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>{saveError}
                    </span>
                  )}
                  {saveSuccess && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <i className="ri-checkbox-circle-line"></i>{saveSuccess}
                    </span>
                  )}
                  <button
                    onClick={handleEnvoyerMedecin}
                    disabled={saving || selectedWithValues === 0 || !selectedBonId}
                    className="px-5 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-sm flex items-center gap-2"
                    title="Prévisualiser le rapport avant envoi"
                  >
                    {saving ? (
                      <><i className="ri-loader-4-line animate-spin"></i>Enregistrement...</>
                    ) : (
                      <><i className="ri-file-list-3-line"></i>Prévisualiser le rapport</>
                    )}
                  </button>
                  <button
                    onClick={handleImprimer}
                    disabled={saving || selectedWithValues === 0 || !selectedBonId}
                    className="px-5 py-3 bg-white border-2 border-teal-600 text-teal-700 rounded-full hover:bg-teal-50 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    title="Aperçu avant impression"
                  >
                    <i className="ri-eye-line"></i>
                    Aperçu avant impression
                  </button>
                </div>
              </div>
            </>
          )}

          {!patient && (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              {/* En-tête de la liste */}
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <i className="ri-group-line text-teal-600"></i>
                  Patients de la clinique
                </h2>
                <span className="text-sm text-gray-500">
                  {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''}
                  {searchTerm && allPatients.length !== filteredPatients.length && (
                    <span className="text-teal-600"> filtré{filteredPatients.length !== 1 ? 's' : ''} sur {allPatients.length}</span>
                  )}
                </span>
              </div>

              {patientsLoading ? (
                <div className="p-16 text-center">
                  <i className="ri-loader-4-line text-3xl text-teal-400 animate-spin"></i>
                  <p className="text-gray-400 text-sm mt-3">Chargement des patients...</p>
                </div>
              ) : filteredPatients.length === 0 ? (
                <div className="p-16 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-user-search-line text-2xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-500 font-medium">Aucun patient trouvé</p>
                  <p className="text-gray-400 text-sm mt-1">
                    {searchTerm ? `Aucun résultat pour "${searchTerm}"` : 'Aucun patient enregistré dans la clinique'}
                  </p>
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilteredPatients(allPatients);
                      }}
                      className="mt-3 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                    >
                      <i className="ri-arrow-left-line mr-1"></i>Réinitialiser la recherche
                    </button>
                  )}
                </div>
              ) : (
                <div className="max-h-[500px] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 p-3">
                    {filteredPatients.map(p => {
                      const ageStr = p.date_naissance ? (() => {
                        const birth = new Date(p.date_naissance);
                        const today = new Date();
                        let age = today.getFullYear() - birth.getFullYear();
                        const m = today.getMonth() - birth.getMonth();
                        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
                        return age < 1 ? `${Math.floor(age * 12)} mois` : `${age} an${age > 1 ? 's' : ''}`;
                      })() : '—';

                      return (
                        <button
                          key={p.id}
                          onClick={() => selectPatient(p)}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-teal-50/80 transition-all cursor-pointer text-left group border border-transparent hover:border-teal-200"
                        >
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                            <span className="text-teal-700 font-bold text-sm">
                              {p.prenom?.[0]}{p.nom?.[0]}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 group-hover:text-teal-700 transition-colors truncate">
                              {p.prenom} {p.nom}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                              <span className="flex items-center gap-0.5">
                                <i className="ri-folder-line text-[10px]"></i>
                                {p.numero_dossier || 'N/A'}
                              </span>
                              <span>·</span>
                              <span>{ageStr}</span>
                              {p.sexe && (
                                <>
                                  <span>·</span>
                                  <span>{p.sexe === 'F' ? 'F' : 'H'}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-teal-500 transition-colors opacity-0 group-hover:opacity-100"></i>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}